/*
* Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
*/

// Based on: https://github.com/nishch/react-ms-login/blob/master/src/index.jsx

// Core imports
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { authentication } from '@microsoft/teams-js';

// Misc imports
import is from '../utils/is';

// Constant declarations
const oauthUrl = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';

const TeamsMicrosoftLoginButton = ({
  clientId,
  redirectUri,
  scopes = ['user.read'],
  responseType = 'token',
  cssClass,
  btnContent = 'Sign in with Microsoft',
  handleLogin
}) => {
  const cssClasses = `btn-microsoft-login ${cssClass}`;

  useEffect(() => {
    if (!clientId) {
      throw new Error('invalid clientId provided for react-ms-login');
    }
    if (!redirectUri) {
      throw new Error('invalid redirectUri provided for react-ms-login');
    }
  }, []);

  /*
  * Builds the oAuthUrl
  */
  const getOAuthUrl = () => {
    const scope = encodeURIComponent(scopes.join(' '));
    const oauthUri = `${oauthUrl}?client_id=${clientId}&response_type=${responseType}`
                + `&redirect_uri=${encodeURIComponent(redirectUri)}`
                + `&scope=${scope}&response_mode=fragment`;

    return oauthUri;
  };

  /*
  * NOTE: The Teams desktop app seems to block all redirects in
  * its authentication pop-ups, so we need to pass the oAuthUrl
  * as a hash to the URL of the html page and force a redirect
  * manually. Also, due to some limitations with the Teams SDK
  * (calling window.close() for the pop-up instantly triggers
  * the failureCallback function), we use failureCallback both
  * for successful and failed logins to extract the response
  * from the local storage (a way to communicate between the
  * pop-up and main window on the same domain)
  */
  const startOAuth = () => {
    authentication.authenticate({
      url: window.location.origin.toString() + '/ms-auth.html#' + getOAuthUrl(),
      width: 700,
      height: 600,
      isExternal: false,
      failureCallback: () => {
        const data = JSON.parse(window.localStorage.getItem('microsoftSsoTeamsFragment') ?? '{}');
        // We remove the fragment entry from the local storage
        window.localStorage.removeItem('microsoftSsoTeamsFragment');
        if (!is.emptyObject(data)) {
          handleLogin(data);
        }
      }
    });
  };

  return (
    <button type="button" className={cssClasses} onClick={startOAuth}>
      {btnContent}
    </button>
  );
};

TeamsMicrosoftLoginButton.propTypes = {
  clientId: PropTypes.string,
  redirectUri: PropTypes.string,
  scopes: PropTypes.array,
  responseType: PropTypes.string,
  cssClass: PropTypes.string,
  btnContent: PropTypes.any,
  handleLogin: PropTypes.func
};

export default TeamsMicrosoftLoginButton;
